<template>
  <div class="demo-interaction-layer" :class="{ active }">
    <button
      v-if="!active"
      ref="enter"
      type="button"
      class="demo-interaction-enter"
      :aria-label="`Open the ${label} interactive demo`"
      @click="$emit('activate')"
    >
      <span class="demo-interaction-cue">
        <span class="demo-interaction-symbol" aria-hidden="true"><span></span></span>
        <span class="demo-interaction-copy">
          <strong>{{ label }}</strong>
          <span>Start exploring</span>
        </span>
        <span class="demo-interaction-chevron" aria-hidden="true">›</span>
      </span>
    </button>
    <div
      v-else
      class="demo-interaction-controls"
      :class="{ 'has-open-action': openHref }"
      aria-label="Interactive demo controls"
    >
      <button
        type="button"
        class="demo-interaction-done"
        :aria-label="`Leave the ${label} interactive demo`"
        @click="$emit('deactivate')"
      >
        <span class="demo-interaction-done-mark" aria-hidden="true"><i></i><i></i></span>
        <span>Done</span>
      </button>
      <a
        v-if="openHref"
        class="demo-interaction-open"
        :href="openHref"
        target="_blank"
        rel="noopener"
      >
        <span>Open full screen</span>
        <span aria-hidden="true"><ArrowUpRightIcon /></span>
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DemoInteractionGate',
  emits: ['activate', 'deactivate'],
  watch: {
    active(value) {
      if (!value) this.$nextTick(() => this.$refs.enter?.focus({ preventScroll: true }));
    },
  },
  props: {
    active: { type: Boolean, default: false },
    label: { type: String, required: true },
    openHref: { type: String, default: '' },
  },
};
</script>

<style scoped>
.demo-interaction-layer { position: absolute; inset: 0; z-index: 60; pointer-events: none; }
.demo-interaction-enter {
  appearance: none;
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  width: 100%;
  padding: 4.5rem 1rem 1rem;
  border: 0;
  background: linear-gradient(to bottom, rgba(250, 250, 252, 0) 48%, rgba(250, 250, 252, 0.12) 64%, rgba(250, 250, 252, 0.88) 100%);
  color: #1d1d1f;
  cursor: pointer;
  pointer-events: auto;
  touch-action: pan-y;
  -webkit-tap-highlight-color: transparent;
}
.demo-interaction-cue {
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr) 1rem;
  align-items: center;
  gap: 0.68rem;
  width: 100%;
  min-height: 2.75rem;
  padding: 0.2rem 0.25rem;
  text-align: left;
  transform: translateY(0) scale(1);
  transform-origin: center bottom;
  transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
}
.demo-interaction-symbol {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #1d1d1f;
  transition: background-color 160ms ease;
}
.demo-interaction-symbol span {
  width: 0;
  height: 0;
  margin-left: 0.12rem;
  border-top: 0.29rem solid transparent;
  border-bottom: 0.29rem solid transparent;
  border-left: 0.43rem solid #f5f5f7;
}
.demo-interaction-copy {
  display: grid;
  gap: 0.08rem;
  min-width: 0;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
}
.demo-interaction-copy strong {
  overflow: hidden;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.012em;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.demo-interaction-copy span {
  color: #6e6e73;
  font-size: 0.69rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.006em;
}
.demo-interaction-chevron {
  color: #86868b;
  font: 400 1.4rem/1 -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif;
  transform: translateX(0);
  transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}
.demo-interaction-enter:active .demo-interaction-cue { transform: scale(0.975); }
.demo-interaction-enter:active .demo-interaction-chevron { transform: translateX(2px); }
.demo-interaction-enter:focus-visible { outline: none; }
.demo-interaction-enter:focus-visible .demo-interaction-symbol { outline: 2px solid currentColor; outline-offset: 3px; }
@media (hover: hover) and (pointer: fine) {
  .demo-interaction-enter:hover .demo-interaction-cue { transform: translateY(-2px); }
  .demo-interaction-enter:hover .demo-interaction-symbol { background: #343436; }
  .demo-interaction-enter:hover .demo-interaction-chevron { transform: translateX(2px); }
  .demo-interaction-enter:active .demo-interaction-cue { transform: scale(0.975); }
}
.demo-interaction-controls {
  position: absolute;
  right: 0.75rem;
  bottom: 0.75rem;
  left: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  color: #1d1d1f;
  pointer-events: none;
}
.demo-interaction-done,
.demo-interaction-open {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: 0.5rem;
  box-sizing: border-box;
  padding: 0.68rem 0.88rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 14px;
  background: rgba(246, 246, 248, 0.92);
  box-shadow: 0 7px 20px rgba(0, 0, 0, 0.11);
  color: inherit;
  font: 600 0.76rem/1 -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  text-decoration: none;
  cursor: pointer;
  pointer-events: auto;
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  -webkit-tap-highlight-color: transparent;
  transition: background-color 160ms ease, border-color 160ms ease, transform 120ms cubic-bezier(0.22, 1, 0.36, 1);
}
.demo-interaction-done:hover,
.demo-interaction-done:focus-visible,
.demo-interaction-open:hover,
.demo-interaction-open:focus-visible {
  background: rgba(255, 255, 255, 0.98);
  border-color: rgba(255, 255, 255, 0.8);
  color: inherit;
}
.demo-interaction-done-mark {
  position: relative;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #1d1d1f;
}
.demo-interaction-done-mark i {
  position: absolute;
  top: 8.5px;
  left: 5px;
  width: 8px;
  height: 1px;
  border-radius: 1px;
  background: #f5f5f7;
}
.demo-interaction-done-mark i:first-child { transform: rotate(45deg); }
.demo-interaction-done-mark i:last-child { transform: rotate(-45deg); }
.demo-interaction-done:active,
.demo-interaction-open:active { transform: scale(0.96); }
.demo-interaction-done:focus-visible,
.demo-interaction-open:focus-visible { outline: 2px solid currentColor; outline-offset: -1px; }
:global(body.dark-mode .demo-interaction-enter) {
  background: linear-gradient(to bottom, rgba(20, 20, 22, 0) 48%, rgba(20, 20, 22, 0.16) 64%, rgba(20, 20, 22, 0.9) 100%);
  color: #f5f5f7;
}
:global(body.dark-mode .demo-interaction-symbol) { background: #f5f5f7; }
:global(body.dark-mode .demo-interaction-symbol span) { border-left-color: #1d1d1f; }
:global(body.dark-mode .demo-interaction-copy span),
:global(body.dark-mode .demo-interaction-chevron) { color: #a1a1a6; }
:global(body.dark-mode .demo-interaction-controls) {
  color: #f5f5f7;
}
:global(body.dark-mode .demo-interaction-done),
:global(body.dark-mode .demo-interaction-open) {
  border-color: rgba(255, 255, 255, 0.11);
  background: rgba(44, 44, 46, 0.94);
  box-shadow: 0 7px 20px rgba(0, 0, 0, 0.3);
}
:global(body.dark-mode .demo-interaction-done:hover),
:global(body.dark-mode .demo-interaction-done:focus-visible),
:global(body.dark-mode .demo-interaction-open:hover),
:global(body.dark-mode .demo-interaction-open:focus-visible) {
  background: rgba(58, 58, 60, 0.98);
  border-color: rgba(255, 255, 255, 0.2);
  color: #f5f5f7;
}
@media (hover: hover) and (pointer: fine) {
  :global(body.dark-mode .demo-interaction-enter:hover .demo-interaction-symbol) { background: #fff; }
}
:global(body.dark-mode .demo-interaction-done-mark) { background: #f5f5f7; }
:global(body.dark-mode .demo-interaction-done-mark i) { background: #1d1d1f; }
@media (prefers-reduced-transparency: reduce) {
  .demo-interaction-enter { background: linear-gradient(to bottom, transparent 42%, #fafafc 100%); }
  :global(body.dark-mode .demo-interaction-enter) { background: linear-gradient(to bottom, transparent 42%, #141416 100%); }
}
@media (prefers-contrast: more) {
  .demo-interaction-copy span,
  .demo-interaction-chevron { color: currentColor; }
}
@media (prefers-reduced-motion: reduce) {
  .demo-interaction-cue,
  .demo-interaction-symbol,
  .demo-interaction-chevron,
  .demo-interaction-done,
  .demo-interaction-open { transition: none; }
}
</style>
